// Bilanci multi-valuta. Ogni spesa/pagamento ha la propria valuta: i bilanci
// si calcolano SEPARATI per valuta (verità contabile, zero conversioni) e,
// on demand, UNIFICATI in una valuta target convertendo ogni singolo
// movimento al tasso storico della SUA data (util/fx.js).
//
// Inputs comuni:
//   group:        { members: [{ id, name, userId }], currency? (legacy fallback) }
//   expenses:     già filtrate sul gruppo
//   settlements:  già filtrati sul gruppo

import { convert } from './fx.js'

const EPS = 0.005  // rounded to cents

// Valuta di un movimento; group.currency è solo il fallback legacy per i
// dati creati quando esisteva la valuta di gruppo.
export function itemCurrency(item, group) {
  return item.currency || group?.currency || 'EUR'
}

// Valute usate almeno una volta nel gruppo, ordinate per frequenza d'uso.
export function usedCurrencies(group, expenses, settlements) {
  const count = new Map()
  for (const it of [...(expenses ?? []), ...(settlements ?? [])]) {
    const c = itemCurrency(it, group)
    count.set(c, (count.get(c) ?? 0) + 1)
  }
  return [...count.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([c]) => c)
}

// Default valuta per un nuovo movimento: l'ultima usata nel gruppo,
// fallback la legacy del gruppo, poi la default utente.
export function lastUsedCurrency(group, expenses, fallback) {
  const list = (expenses ?? [])
    .filter(e => e.groupId === group?.id)
    .sort((a, b) => (b.createdAt ?? b.date ?? '').localeCompare(a.createdAt ?? a.date ?? ''))
  return list[0]?.currency || group?.currency || fallback || 'EUR'
}

// Quote per member nella valuta ORIGINALE della spesa.
function splitShares(expense) {
  const total = expense.amount
  const participants = expense.participants ?? []
  const mode = expense.splitMode ?? 'equal'

  if (participants.length === 0) return new Map()

  const shares = new Map()

  if (mode === 'exact') {
    for (const p of participants) shares.set(p.memberId, Number(p.value) || 0)
    return shares
  }
  if (mode === 'percent') {
    for (const p of participants) {
      shares.set(p.memberId, (Number(p.value) || 0) * total / 100)
    }
    return shares
  }
  if (mode === 'shares') {
    const sum = participants.reduce((a, p) => a + (Number(p.value) || 0), 0) || 1
    for (const p of participants) {
      shares.set(p.memberId, (Number(p.value) || 0) * total / sum)
    }
    return shares
  }
  // 'adjust' (by adjustment, stile Splitwise): `value` = extra personale
  // (es. il dolce preso solo da uno). Gli extra si scalano dal totale, il
  // resto si divide in parti uguali e a ognuno si riaggiunge il suo extra.
  if (mode === 'adjust') {
    const sumAdj = participants.reduce((a, p) => a + (Number(p.value) || 0), 0)
    const rest = (total - sumAdj) / participants.length
    for (const p of participants) {
      shares.set(p.memberId, (Number(p.value) || 0) + rest)
    }
    return shares
  }
  // equal + fallback
  const per = total / participants.length
  for (const p of participants) shares.set(p.memberId, per)
  return shares
}

// Quota di un singolo member su una spesa (per la riga "devi X" in Activity)
export function memberShareOf(expense, memberId) {
  const shares = splitShares(expense)
  return Math.round((shares.get(memberId) ?? 0) * 100) / 100
}

// Paganti di una spesa: `payers[] = [{ memberId, amount }]` (multi-pagante,
// dal 2026-07-21). Le spese con un solo pagante hanno il legacy `paidBy` e
// nessun payers → normalizzate qui in una lista da un elemento.
// `amount` è SEMPRE nella valuta della spesa: chi ha anticipato in un'altra
// valuta porta anche origAmount/origCurrency, che sono informativi (quanto gli
// è stato addebitato davvero). La conversione è congelata al salvataggio, così
// i bilanci non cambiano se domani cambia il tasso.
export function expensePayers(expense) {
  const list = (expense?.payers ?? [])
    .map(p => ({
      memberId: p?.memberId,
      amount: Number(p?.amount) || 0,
      origAmount: Number.isFinite(Number(p?.origAmount)) ? Number(p.origAmount) : null,
      origCurrency: p?.origCurrency || null,
    }))
    .filter(p => p.memberId && p.amount > 0.004)
  if (list.length) return list
  return expense?.paidBy
    ? [{ memberId: expense.paidBy, amount: Number(expense.amount) || 0 }]
    : []
}

export function isMultiPayer(expense) {
  return (expense?.payers ?? []).filter(p => Number(p?.amount) > 0.004).length > 1
}

// Quanto ha anticipato un member su una spesa (0 se non ha pagato)
export function paidAmountOf(expense, memberId) {
  const p = expensePayers(expense).find(x => x.memberId === memberId)
  return p ? Math.round(p.amount * 100) / 100 : 0
}

// Ledger singolo su una lista di movimenti. amountOf(item) → importo da
// usare (originale o convertito); null = tasso mancante → skip + flag.
function computeLedger(group, expenses, settlements, amountOf) {
  const net = new Map()
  for (const m of group.members ?? []) net.set(m.id, 0)
  let missing = false

  for (const exp of expenses) {
    const amt = amountOf(exp)
    if (amt == null) { missing = true; continue }
    // Quote e anticipi scalano linearmente col totale convertito (vale anche
    // per 'exact': i valori sono nella valuta originale della spesa)
    const factor = exp.amount ? amt / exp.amount : 1
    // Accredito a ogni pagante quanto ha anticipato (multi-pagante)
    for (const p of expensePayers(exp)) {
      net.set(p.memberId, (net.get(p.memberId) ?? 0) + p.amount * factor)
    }
    for (const [memberId, share] of splitShares(exp)) {
      net.set(memberId, (net.get(memberId) ?? 0) - share * factor)
    }
  }
  for (const s of settlements) {
    const amt = amountOf(s)
    if (amt == null) { missing = true; continue }
    net.set(s.from, (net.get(s.from) ?? 0) + amt)
    net.set(s.to,   (net.get(s.to)   ?? 0) - amt)
  }

  for (const k of net.keys()) net.set(k, Math.round(net.get(k) * 100) / 100)
  return { net, debts: simplify(net), missing }
}

// Bilanci separati: un ledger indipendente per ogni valuta usata.
//   → { currencies: [code], ledgers: { [code]: { net, debts } } }
export function computeBalancesByCurrency({ group, expenses, settlements }) {
  const currencies = usedCurrencies(group, expenses, settlements)
  const ledgers = {}
  for (const cur of currencies) {
    ledgers[cur] = computeLedger(
      group,
      (expenses ?? []).filter(e => itemCurrency(e, group) === cur),
      (settlements ?? []).filter(s => itemCurrency(s, group) === cur),
      it => it.amount,
    )
  }
  return { currencies, ledgers }
}

// Vista unificata: ogni movimento convertito in `target` al tasso storico
// della sua data. ratesByDate = { 'YYYY-MM-DD': rates } (da getRatesForDates).
//   → { net, debts, missing } (missing = qualche tasso non disponibile)
export function computeBalancesConverted({ group, expenses, settlements, target, ratesByDate }) {
  const amountOf = (it) => {
    const from = itemCurrency(it, group)
    if (from === target) return it.amount
    const rates = ratesByDate?.[(it.date || '').slice(0, 10)]
    return convert(it.amount, from, target, rates)
  }
  return computeLedger(group, expenses ?? [], settlements ?? [], amountOf)
}

// Saldo PAIRWISE tra `youId` e ogni altro member del gruppo:
//   net.get(x) > 0  →  x deve a you
//   net.get(x) < 0  →  you deve a x
//
// Non è `debts` (che è la semplificazione greedy: ottima per "chi paga chi"
// dentro un gruppo, ma reindirizza i debiti verso terzi e quindi non dice
// quanto devi a una persona specifica). Qui ogni spesa si scompone nella
// relazione reale: chi partecipa deve a chi ha anticipato, in proporzione a
// quanto ciascun pagante ha messo. La somma dei pairwise di you è per
// costruzione uguale al suo net nel ledger.
export function pairwiseNet({ group, expenses, settlements, youId, amountOf }) {
  const net = new Map()
  if (!youId) return { net, missing: false }
  let missing = false
  const add = (id, v) => {
    if (!id || id === youId) return
    net.set(id, (net.get(id) ?? 0) + v)
  }

  for (const exp of expenses ?? []) {
    const amt = amountOf(exp)
    if (amt == null) { missing = true; continue }
    const factor = exp.amount ? amt / exp.amount : 1
    const shares = splitShares(exp)
    const payers = expensePayers(exp)
    const totalPaid = payers.reduce((a, p) => a + p.amount, 0) || exp.amount || 0
    if (!totalPaid) continue

    const youShare = shares.get(youId) ?? 0
    const youPaid  = payers.find(p => p.memberId === youId)?.amount ?? 0

    // La tua quota è stata anticipata dai paganti, pro quota → devi a loro
    if (youShare > 0) {
      for (const p of payers) {
        if (p.memberId === youId) continue
        add(p.memberId, -youShare * (p.amount / totalPaid) * factor)
      }
    }
    // Quello che hai anticipato tu copre le quote altrui → loro devono a te
    if (youPaid > 0) {
      for (const [mid, share] of shares) {
        if (mid === youId) continue
        add(mid, share * (youPaid / totalPaid) * factor)
      }
    }
  }

  for (const s of settlements ?? []) {
    const amt = amountOf(s)
    if (amt == null) { missing = true; continue }
    if (s.from === youId)    add(s.to, amt)      // hai pagato → il debito cala
    else if (s.to === youId) add(s.from, -amt)   // ti ha pagato → il credito cala
  }

  for (const k of net.keys()) net.set(k, Math.round(net.get(k) * 100) / 100)
  return { net, missing }
}

// Debiti REALI fra ogni coppia: chi ha partecipato deve a chi ha anticipato,
// pro quota di quanto ciascun pagante ha messo. È la stessa scomposizione di
// pairwiseNet, estesa a tutte le coppie invece che alla sola "you".
//
// Diverso da `simplify`, che produce il piano di pagamento più corto
// reindirizzando i debiti verso terzi: quello vale solo se TUTTI saldano
// insieme: chi paga da solo la propria riga semplificata resta in debito con
// gli altri senza capire perché.
export function pairwiseDebts({ group, expenses, settlements, amountOf }) {
  // chiave ordinata "a|b" → quanto a deve a b (negativo = il contrario)
  const m = new Map()
  const add = (debtor, creditor, v) => {
    if (!debtor || !creditor || debtor === creditor || !v) return
    const flip = debtor > creditor
    const key = flip ? `${creditor}|${debtor}` : `${debtor}|${creditor}`
    m.set(key, (m.get(key) ?? 0) + (flip ? -v : v))
  }
  let missing = false

  for (const exp of expenses ?? []) {
    const amt = amountOf(exp)
    if (amt == null) { missing = true; continue }
    const factor = exp.amount ? amt / exp.amount : 1
    const shares = splitShares(exp)
    const payers = expensePayers(exp)
    const totalPaid = payers.reduce((a, p) => a + p.amount, 0) || exp.amount || 0
    if (!totalPaid) continue
    for (const p of payers) {
      for (const [mid, share] of shares) {
        if (mid === p.memberId) continue
        add(mid, p.memberId, share * (p.amount / totalPaid) * factor)
      }
    }
  }
  for (const s of settlements ?? []) {
    const amt = amountOf(s)
    if (amt == null) { missing = true; continue }
    add(s.to, s.from, amt)   // pagando, `from` matura credito verso `to`
  }

  const debts = []
  for (const [key, v] of m) {
    const [a, b] = key.split('|')
    const r = Math.round(v * 100) / 100
    if (Math.abs(r) < EPS) continue
    debts.push(r > 0 ? { from: a, to: b, amount: r } : { from: b, to: a, amount: -r })
  }
  return { debts: debts.sort((x, y) => y.amount - x.amount), missing }
}

// Riepilogo "you" da una lista di debts già calcolata.
export function youOweFromDebts(debts, youId) {
  if (!youId) return { youOwe: 0, youAreOwed: 0, net: 0 }
  let youOwe = 0, youAreOwed = 0
  for (const d of debts) {
    if (d.from === youId) youOwe += d.amount
    if (d.to === youId) youAreOwed += d.amount
  }
  return {
    youOwe: Math.round(youOwe * 100) / 100,
    youAreOwed: Math.round(youAreOwed * 100) / 100,
    net: Math.round((youAreOwed - youOwe) * 100) / 100,
  }
}

// Riepilogo "you" per valuta: [{ currency, youOwe, youAreOwed, net }]
// (tutte le valute usate; il chiamante filtra i net a zero se vuole).
export function youSummaryByCurrency(group, expenses, settlements, youId) {
  const { currencies, ledgers } = computeBalancesByCurrency({ group, expenses, settlements })
  return currencies.map(currency => ({
    currency,
    ...youOweFromDebts(ledgers[currency].debts, youId),
  }))
}

// Greedy simplification: repeatedly match largest creditor with largest debtor.
// Produces O(n) transactions instead of O(n²) (no cycles).
function simplify(net) {
  const creditors = []
  const debtors   = []
  for (const [id, bal] of net) {
    if (bal > EPS)  creditors.push({ id, bal })
    if (bal < -EPS) debtors.push({ id, bal: -bal })
  }
  creditors.sort((a, b) => b.bal - a.bal)
  debtors.sort((a, b) => b.bal - a.bal)

  const debts = []
  let i = 0, j = 0
  while (i < debtors.length && j < creditors.length) {
    const d = debtors[i]
    const c = creditors[j]
    const amt = Math.min(d.bal, c.bal)
    if (amt > EPS) {
      debts.push({ from: d.id, to: c.id, amount: Math.round(amt * 100) / 100 })
    }
    d.bal -= amt
    c.bal -= amt
    if (d.bal < EPS) i++
    if (c.bal < EPS) j++
  }
  return debts
}
